/**
 * @author        kyle.agronick@zumper.com (Kyle Agronick)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   Zumper header
 */

import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware/actions'
import type { Dispatch } from '@zcode/nr/es/shared/types'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { TRACK_NAVBAR_ACTION } from '../constants'
import { UserMenuContext, type UserMenuContextProperties } from '../module/context/UserMenuContext'
import { trackUserMenuClick } from '../module/thunks/trackUserMenuClick'
import type { HeaderProps } from '../types'
import { HeaderDesktop } from './HeaderDesktop/HeaderDesktop'
import { HeaderMobileStr } from './HeaderMobileStr'
import { HomepageHeaderDesktop } from './HomepageHeaderDesktop'
import { HomepageHeaderMobile } from './HomepageHeaderMobile'

export const Header = ({
  handleStayLengthChange,
  isCurrentSearchView,
  isDetailView,
  isHomepageView,
  isLoggedIn,
  isMobile,
  locationName,
  mixpanelEvent,
  onNavLinkClick,
  onSearchClick,
  getPageNameFromRouteProps,
  toolsActivated,
  ...restProps
}: HeaderProps) => {
  const dispatch = useDispatch<Dispatch>()
  const [isSideMenuShowing, setIsSideMenuShowing] = useState(false)
  const toggleMenu = useCallback(
    () => setIsSideMenuShowing(!isSideMenuShowing),
    [isSideMenuShowing]
  )

  const onSearchButtonClick = useCallback(() => {
    onNavLinkClick?.({
      trackAction: TRACK_NAVBAR_ACTION.SEARCH,
    })
    onSearchClick?.()
    setIsSideMenuShowing(false)
  }, [onSearchClick, onNavLinkClick])

  const onEscPressed = useCallback(() => {
    setIsSideMenuShowing(false)
  }, [])

  const passedProps = {
    isSideMenuShowing,
    onSearchButtonClick,
    isMobile,
    isCurrentSearchView,
    isDetailView,
    isHomepageView,
    locationName,
    mixpanelEvent,
    toggleMenu,
    onEscPressed,
    onNavLinkClick,
    ...restProps,
  }
  const contextValue = useMemo(
    () =>
      ({
        dispatchTrackUserMenuClick: (linkClicked: string) => {
          dispatch(
            trackUserMenuClick(
              linkClicked,
              getPageNameFromRouteProps({ routeProps: dispatch(getRouteProps()), isMobile })
            )
          )
        },
        handleStayLengthChange,
        isCurrentSearchView,
        isLoggedIn: !!isLoggedIn,
        toolsActivated,
      }) satisfies UserMenuContextProperties,
    [
      dispatch,
      isLoggedIn,
      isMobile,
      getPageNameFromRouteProps,
      toolsActivated,
      handleStayLengthChange,
      isCurrentSearchView,
    ]
  )

  return (
    <UserMenuContext.Provider value={contextValue}>
      {isHomepageView ? (
        isMobile ? (
          <HomepageHeaderMobile {...passedProps} />
        ) : (
          <HomepageHeaderDesktop {...passedProps} />
        )
      ) : isMobile ? (
        <HeaderMobileStr {...passedProps} isCurrentSearchView={isCurrentSearchView} />
      ) : (
        <HeaderDesktop {...passedProps} isCurrentSearchView={isCurrentSearchView} />
      )}
    </UserMenuContext.Provider>
  )
}
